/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetOrganizationRemoteSessionIssuerRequest,
  GetOrganizationRemoteSessionIssuerSecurity,
} from "../models/operations/getorganizationremotesessionissuer.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildOrganizationRemoteSessionIssuerQuery,
  OrganizationRemoteSessionIssuerQueryData,
  prefetchOrganizationRemoteSessionIssuer,
  queryKeyOrganizationRemoteSessionIssuer,
} from "./organizationRemoteSessionIssuer.core.js";
export {
  buildOrganizationRemoteSessionIssuerQuery,
  type OrganizationRemoteSessionIssuerQueryData,
  prefetchOrganizationRemoteSessionIssuer,
  queryKeyOrganizationRemoteSessionIssuer,
};

export type OrganizationRemoteSessionIssuerQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getIssuer organizationRemoteSessionIssuers
 *
 * @remarks
 * Get any remote_session_issuer (organizational or project-specific) in the caller's organization by id. Requires org:read.
 */
export function useOrganizationRemoteSessionIssuer(
  request: GetOrganizationRemoteSessionIssuerRequest,
  security?: GetOrganizationRemoteSessionIssuerSecurity | undefined,
  options?: QueryHookOptions<
    OrganizationRemoteSessionIssuerQueryData,
    OrganizationRemoteSessionIssuerQueryError
  >,
): UseQueryResult<
  OrganizationRemoteSessionIssuerQueryData,
  OrganizationRemoteSessionIssuerQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildOrganizationRemoteSessionIssuerQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getIssuer organizationRemoteSessionIssuers
 *
 * @remarks
 * Get any remote_session_issuer (organizational or project-specific) in the caller's organization by id. Requires org:read.
 */
export function useOrganizationRemoteSessionIssuerSuspense(
  request: GetOrganizationRemoteSessionIssuerRequest,
  security?: GetOrganizationRemoteSessionIssuerSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    OrganizationRemoteSessionIssuerQueryData,
    OrganizationRemoteSessionIssuerQueryError
  >,
): UseSuspenseQueryResult<
  OrganizationRemoteSessionIssuerQueryData,
  OrganizationRemoteSessionIssuerQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildOrganizationRemoteSessionIssuerQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setOrganizationRemoteSessionIssuerData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      id: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
    },
  ],
  data: OrganizationRemoteSessionIssuerQueryData,
): OrganizationRemoteSessionIssuerQueryData | undefined {
  const key = queryKeyOrganizationRemoteSessionIssuer(...queryKeyBase);

  return client.setQueryData<OrganizationRemoteSessionIssuerQueryData>(
    key,
    data,
  );
}

export function invalidateOrganizationRemoteSessionIssuer(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      id: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "organizationRemoteSessionIssuers",
      "get",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllOrganizationRemoteSessionIssuer(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "organizationRemoteSessionIssuers", "get"],
  });
}
